import type { ReactElement } from 'react';
import { holdsPermission, type Permission } from '@/api/permissions';

/**
 * The navigation vocabulary and the one function that decides visibility.
 *
 * The entries themselves live with their module, in `src/modules/*.tsx`: this
 * file holds no module's data, so marketplace, invoicing and the product
 * catalogue each add a file instead of all three editing one.
 */

/**
 * Who a nav entry is for. Roles never appear here: a role is only the set of
 * permissions and the `is_platform` flag the backend hands out in the JWT, so
 * a new role is described by data the entries already declare, not by a new
 * branch. `any` is the default.
 */
export type NavAudience = 'platform' | 'tenant' | 'any';

export interface NavItem {
  key: string;
  labelKey: string;
  to: string;
  icon: ReactElement;
  /** Defaults to `'any'`. */
  audience?: NavAudience;
  /** Shown when the viewer holds any of these. Omitted means no permission needed. */
  anyOf?: Permission[];
  /**
   * A feature this deployment may not have. The entry is drawn only when the
   * deployment says it has it — never inferred from a 404 — so a console
   * without tracking shows no map entry rather than a broken one.
   */
  requires?: NavCapability;
}

/**
 * Optional backend features, each answered by its own unconditional
 * availability endpoint. Not a permission and not an audience: a permission
 * says who may, this says whether the deployment can at all.
 */
export type NavCapability = 'tracking';

export interface NavSection {
  key: string;
  labelKey?: string;
  /**
   * Applies to the whole section, header included, before its items are
   * looked at. Permissions alone cannot express "platform staff": a tenant
   * owner holds `ORGANIZATION_READ` too. Defaults to `'any'`.
   */
  audience?: NavAudience;
  items: NavItem[];
}

/** What the sidebar knows about the signed-in user — nothing more is needed. */
export interface NavViewer {
  /** The role's `is_platform` flag (`plat` JWT claim). */
  isPlatform: boolean;
  /** Permission codes from the `perms` JWT claim. */
  permissions: ReadonlySet<string>;
  /**
   * The optional features this deployment has said it has. Absent or not
   * listed means not drawn: unknown is not available.
   */
  capabilities?: ReadonlySet<NavCapability>;
}

/**
 * The gate any surface — the sidebar, the modules landing — declares on an
 * entry. There is one of these in the app on purpose: two answers to "may
 * this person see this door" drift apart the week after they are written.
 */
export interface NavGate {
  audience?: NavAudience;
  anyOf?: Permission[];
}

function audienceMatches(audience: NavAudience | undefined, viewer: NavViewer): boolean {
  switch (audience ?? 'any') {
    case 'platform':
      return viewer.isPlatform;
    case 'tenant':
      return !viewer.isPlatform;
    case 'any':
      return true;
  }
}

function permissionMatches(anyOf: Permission[] | undefined, viewer: NavViewer): boolean {
  return !anyOf || anyOf.some((permission) => holdsPermission(viewer.permissions, permission));
}

/** Does this viewer pass the entry's audience *and* its permission list? */
export function viewerMatches(gate: NavGate, viewer: NavViewer): boolean {
  return audienceMatches(gate.audience, viewer) && permissionMatches(gate.anyOf, viewer);
}

/**
 * The one place navigation visibility is decided. Pure: the same definition
 * and viewer always give the same answer. A section whose audience does not
 * match is dropped whole; otherwise each item must match its own audience,
 * hold one of its `anyOf` permissions and — when it `requires` a capability —
 * be on a deployment that has it; a section left empty is dropped too.
 */
export function visibleNavigation(sections: NavSection[], viewer: NavViewer): NavSection[] {
  return sections.flatMap((section) => {
    if (!audienceMatches(section.audience, viewer)) return [];
    const items = section.items.filter(
      (item) => viewerMatches(item, viewer) && (!item.requires || Boolean(viewer.capabilities?.has(item.requires))),
    );
    return items.length ? [{ ...section, items }] : [];
  });
}
